import React, { useEffect, useState } from 'react';
import { AVATARS, parseAvatar } from '../data/avatars';
import '../styles/avatar.css';

/* --- Single avatar render --- */
export function Avatar({ value, name, size = 56, className = '' }) {
  const parsed = parseAvatar(value);
  const style = { width: size, height: size, fontSize: Math.max(14, size * 0.45) };

  if (parsed.kind === 'url') {
    return (
      <span className={`adeeb-avatar adeeb-avatar-img ${className}`} style={style}>
        <img src={parsed.value} alt="" draggable={false} />
      </span>
    );
  }

  if (parsed.kind === 'preset') {
    const { preset } = parsed;
    return (
      <span
        className={`adeeb-avatar adeeb-avatar-preset ${className}`}
        style={{ ...style, background: `linear-gradient(135deg, ${preset.from}, ${preset.to})` }}
        aria-label={preset.label}
      >
        <img
          className="adeeb-avatar-glyph"
          src={`/avatars/${preset.id}.svg`}
          alt=""
          draggable={false}
          aria-hidden="true"
          loading="lazy"
        />
      </span>
    );
  }

  // Empty: initials fallback
  const initials = (name || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]).join('').toUpperCase() || '?';
  return (
    <span className={`adeeb-avatar adeeb-avatar-initials ${className}`} style={style} aria-hidden="true">
      {initials}
    </span>
  );
}

/* --- Picker modal --- */
export function AvatarPicker({ open, currentValue, onClose, onPick, saving = false }) {
  const [hover, setHover] = useState(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const stop = (e) => e.stopPropagation();

  return (
    <div className="avatar-picker-backdrop" onClick={onClose}>
      <div className="avatar-picker" role="dialog" aria-label="Choose an avatar" onClick={stop}>
        <div className="avatar-picker-head">
          <h2 className="avatar-picker-title">Pick an avatar</h2>
        </div>
        <div className="avatar-picker-grid">
          {AVATARS.map((a) => {
            const isCurrent = a.id === currentValue;
            return (
              <button
                key={a.id}
                type="button"
                className={`avatar-picker-tile ${isCurrent ? 'is-current' : ''} ${hover === a.id ? 'is-hover' : ''}`}
                onMouseEnter={() => setHover(a.id)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onPick?.(a.id)}
                disabled={saving}
                aria-label={a.label}
                title={a.label}
              >
                <span
                  className="avatar-picker-tile-bg"
                  style={{ background: `linear-gradient(135deg, ${a.from}, ${a.to})` }}
                  aria-hidden="true"
                />
                <img
                  className="avatar-picker-tile-glyph"
                  src={`/avatars/${a.id}.svg`}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  draggable={false}
                />
                {isCurrent && (
                  <span className="avatar-picker-tile-check" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="5 12 10 17 19 7" />
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="avatar-picker-foot">
          <span className="avatar-picker-hint">{saving ? 'Saving…' : 'Click any avatar to apply'}</span>
        </div>
      </div>
    </div>
  );
}
